import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Eye,
  Trash2,
  Loader2,
  Download,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Webhook,
  Percent,
  FileCode,
} from 'lucide-react';
import { useFormResponses } from '../../hooks/useFormResponses';
import { useForm } from '../../hooks/useForms';
import { Button } from '../Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { EmptyState } from '../ui/EmptyState';
import { StatsCard } from '../ui/StatsCard';
import { Modal } from '../ui/Modal';
import { Reveal } from '../Reveal';
import { ResponseScoreViewer } from './ResponseScoreViewer';
import { WebhookLogViewer } from './WebhookLogViewer';
// Story saas-1.19 Task 6 — PDF exporters loaded lazily inside click handlers
// (D5 spec): @react-pdf/renderer + html2canvas/jspdf live in code-split chunks
// to keep TTI of /responses unchanged. Routing v1 vs v2 via isFeatureEnabled
// (batch multi-página e relatório analítico). O export INDIVIDUAL migrou pro
// deck Vecta (lib/pdf/respostas-pdf, handoff 2026-07-18) — sem flag.
import { isFeatureEnabled } from '../../lib/featureFlags';
import { useAuth } from '../../hooks/useAuth';
import type { ReportConfig } from '../../lib/pdf/ReportPDFTemplate';
import { exportResponsesToCSV } from '../../lib/csv/csvExporter';
import { downloadResponseMarkdown, downloadResponsesMarkdown } from '../../lib/markdown/markdownExporter';
import { useUpgradeGate } from '../billing/UpgradeGate';
import { ExportFormatModal } from './ExportFormatModal';
import { ReportConfigModal } from './ReportConfigModal';
import type { FormResponse, ResponseStatus } from '../../types/forms';

// Caps from saas-1.17 OQ1 (Pax): soft warning at 50, hard block at 200.
const BATCH_SOFT_CAP = 50;
const BATCH_HARD_CAP = 200;

const STATUS_CONFIG: Record<ResponseStatus, { label: string; color: string; icon: React.ElementType }> = {
  new: { label: 'Novo', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: AlertCircle },
  in_progress: { label: 'Em andamento', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  completed: { label: 'Concluído', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2 },
};

const BATCH_DELETE_OPTIONS = [
  { value: '7', label: 'Mais de 7 dias' },
  { value: '14', label: 'Mais de 14 dias' },
  { value: '30', label: 'Mais de 30 dias' },
];

interface FormResponsesProps {
  embedded?: boolean;
}

export const FormResponses: React.FC<FormResponsesProps> = ({ embedded = false }) => {
  const { id: formId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { form, loading: formLoading, error: formError } = useForm(formId);

  // URL param persistence for status filter (AC2)
  const initialStatus = (searchParams.get('status') as ResponseStatus | 'all') || 'all';
  const [statusFilter, setStatusFilter] = useState<ResponseStatus | 'all'>(initialStatus);

  const [activeView, setActiveView] = useState<'responses' | 'webhookLogs'>('responses');
  const [deleteModal, setDeleteModal] = useState<FormResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedResponseId, setExpandedResponseId] = useState<string | null>(null);
  const [batchDeleteModal, setBatchDeleteModal] = useState(false);
  const [batchDeleteDays, setBatchDeleteDays] = useState('7');
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  const [exportingPdfId, setExportingPdfId] = useState<string | null>(null);
  // Export individual no deck Vecta (2026-07-18): erro inline em vez de alert.
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [exportingMdId, setExportingMdId] = useState<string | null>(null);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  // Multi-select + batch export (saas-1.17)
  const [selectedResponses, setSelectedResponses] = useState<Set<string>>(new Set());
  const [isFormatModalOpen, setIsFormatModalOpen] = useState(false);
  // saas-PDF-C: analytical report config + generation state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportProgressLabel, setReportProgressLabel] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);

  // Sync status filter to URL params (AC2)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (statusFilter === 'all') {
      params.delete('status');
    } else {
      params.set('status', statusFilter);
    }
    setSearchParams(params, { replace: true });
  }, [statusFilter, searchParams, setSearchParams]);

  const { responses, loading, error: responsesError, stats, updateResponse, deleteResponse } = useFormResponses(
    formId,
    { status: statusFilter === 'all' ? undefined : statusFilter }
  );

  const error = formError || responsesError;

  // Total fields from form definition
  const totalFields = useMemo(() => {
    const fields = form?.fields;
    if (!Array.isArray(fields)) return 0;
    return fields.length;
  }, [form?.fields]);

  // Compute completion rate (AC5)
  const completionRate = useMemo(() => {
    const total = stats.completed + stats.inProgress;
    if (total === 0) return 0;
    return Math.round((stats.completed / total) * 100);
  }, [stats.completed, stats.inProgress]);

  const abandonedCount = stats.abandoned;

  const getFieldCount = (response: FormResponse) => {
    const answered = Object.keys(response.data || {}).length;
    return { answered, total: totalFields };
  };

  const getProgressPercent = (response: FormResponse) => {
    const { answered, total } = getFieldCount(response);
    if (total === 0) return 0;
    return Math.round((answered / total) * 100);
  };

  const handleStatusChange = async (responseId: string, newStatus: ResponseStatus) => {
    await updateResponse(responseId, { status: newStatus });
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setIsDeleting(true);
    await deleteResponse(deleteModal.id);
    setIsDeleting(false);
    setDeleteModal(null);
  };

  const { ensureFeature } = useUpgradeGate();
  const { user } = useAuth();

  const handleExportPDF = async (response: FormResponse) => {
    if (!form) return;
    if (
      !ensureFeature(
        'pdf.individual',
        'pro',
        'Exportação de respostas em PDF está disponível nos planos Pro e Business.'
      )
    ) {
      return;
    }
    setPdfError(null);
    setExportingPdfId(response.id);
    try {
      // Export individual no deck Vecta (handoff 2026-07-18): HTML auto-contido
      // → edge fn respostas-pdf → Gotenberg. Substitui jspdf/html2canvas e
      // @react-pdf NESTE fluxo; batch multi-página e relatório analítico
      // continuam no caminho v1/v2 abaixo. Import lazy: o chunk carrega
      // fontes+logo em base64 (~250KB) e não pode entrar no TTI da rota.
      const mod = await import('../../lib/pdf/respostas-pdf');
      await mod.baixarRespostaPdf(response, form);
    } catch (err) {
      console.error('[FormResponses] Falha ao exportar PDF:', err);
      setPdfError(err instanceof Error && err.message ? err.message : 'Falha ao gerar PDF. Tente novamente.');
    } finally {
      setExportingPdfId(null);
    }
  };

  const handleExportCsv = () => {
    if (!form || isExportingCsv) return;
    setIsExportingCsv(true);
    try {
      exportResponsesToCSV(responses, form);
    } catch (err) {
      console.error('[FormResponses] Falha ao exportar CSV:', err);
      alert('Falha ao gerar CSV. Tente novamente.');
    } finally {
      setIsExportingCsv(false);
    }
  };

  // Story saas-1.18: single-response Markdown export.
  const handleExportMarkdown = (response: FormResponse) => {
    if (!form) return;
    if (
      // tierRequired='pro' is a placeholder for the upgrade modal (TierTarget excludes 'free').
      // FEATURE_GATES['md.export'] includes 'free', so the gate always passes for all tiers
      // until Morgan/Pax flip it via plans.features JSONB (Story saas-1.18 D5).
      !ensureFeature(
        'md.export',
        'pro',
        'Exportação de respostas em Markdown está disponível em todos os planos.'
      )
    ) {
      return;
    }
    setExportingMdId(response.id);
    try {
      downloadResponseMarkdown(response, form);
    } catch (err) {
      console.error('[FormResponses] Falha ao exportar MD:', err);
      alert('Falha ao gerar Markdown. Tente novamente.');
    } finally {
      setExportingMdId(null);
    }
  };

  // Multi-select handlers (saas-1.17)
  const toggleResponseSelection = (id: string) => {
    setSelectedResponses((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedResponses((prev) => {
      if (prev.size === responses.length && responses.length > 0) {
        return new Set();
      }
      return new Set(responses.map((r) => r.id));
    });
  };

  const handleBatchExportClick = () => {
    if (!form || selectedResponses.size === 0 || batchProgress) return;
    if (selectedResponses.size > BATCH_HARD_CAP) {
      alert(`Máximo de ${BATCH_HARD_CAP} respostas por exportação. Filtre ou desmarque algumas.`);
      return;
    }
    if (selectedResponses.size > BATCH_SOFT_CAP) {
      const ok = window.confirm(
        `Exportação de ${selectedResponses.size} respostas pode demorar. Continuar?`
      );
      if (!ok) return;
    }
    if (selectedResponses.size === 1) {
      // Direct single-response export (OQ3: size === 1 → no modal). Defaults to PDF
      // (existing behavior); MD-only path is via row-level "Exportar MD" button.
      // pdf.individual gate is enforced inside handleExportPDF.
      const [onlyId] = Array.from(selectedResponses);
      const onlyResponse = responses.find((r) => r.id === onlyId);
      if (onlyResponse) handleExportPDF(onlyResponse);
      return;
    }
    // N >= 2: open modal — each format card enforces its own plan gate
    // (PDF=pro, Analytical=business, MD=free per saas-1.18 D5).
    setIsFormatModalOpen(true);
  };

  const handleBatchExportMultiPage = async () => {
    if (!form || selectedResponses.size === 0 || batchProgress) return;
    if (
      !ensureFeature(
        'pdf.individual',
        'pro',
        'Exportação de respostas em PDF está disponível nos planos Pro e Business.'
      )
    ) {
      setIsFormatModalOpen(false);
      return;
    }
    const selectedList = responses.filter((r) => selectedResponses.has(r.id));
    setIsFormatModalOpen(false);
    setBatchProgress({ current: 0, total: selectedList.length });
    try {
      const useV2 = isFeatureEnabled('pdf.v2', user?.id);
      const mod = useV2
        ? await import('../../lib/pdf/v2/pdfExporter')
        : await import('../../lib/pdf/pdfExporter');
      const result = await mod.exportResponsesToPDF(selectedList, form, {
        onProgress: (currentIndex, total) => {
          setBatchProgress({ current: currentIndex, total });
        },
      });
      if (result.skippedIds.length > 0) {
        alert(
          `PDF gerado com ${result.succeeded} de ${result.total} respostas. ${result.skippedIds.length} falha${result.skippedIds.length === 1 ? '' : 's'} — veja console para detalhes.`
        );
      }
    } catch (err) {
      console.error('[FormResponses] Falha ao exportar PDFs em lote:', err);
      alert('Falha ao gerar PDF em lote. Tente novamente.');
    } finally {
      setBatchProgress(null);
    }
  };

  // Story saas-1.18: batch Markdown export (concat-único per D3).
  const handleBatchExportMarkdown = () => {
    if (!form || selectedResponses.size === 0 || batchProgress) return;
    if (
      // tierRequired='pro' is a placeholder for the upgrade modal (TierTarget excludes 'free').
      // FEATURE_GATES['md.export'] includes 'free', so the gate always passes for all tiers
      // until Morgan/Pax flip it via plans.features JSONB (Story saas-1.18 D5).
      !ensureFeature(
        'md.export',
        'pro',
        'Exportação de respostas em Markdown está disponível em todos os planos.'
      )
    ) {
      setIsFormatModalOpen(false);
      return;
    }
    const selectedList = responses.filter((r) => selectedResponses.has(r.id));
    setIsFormatModalOpen(false);
    try {
      downloadResponsesMarkdown(selectedList, form);
    } catch (err) {
      console.error('[FormResponses] Falha ao exportar MD em lote:', err);
      alert('Falha ao gerar Markdown em lote. Tente novamente.');
    }
  };

  // saas-PDF-C: handler do Card 2 "Analítico com gráficos" no ExportFormatModal
  const handleSelectAnalytical = () => {
    if (!form) return;
    // Gate pdf.report (business only) — Free/Pro disparam UpgradeModal
    if (
      !ensureFeature(
        'pdf.report',
        'business',
        'Relatórios analíticos com gráficos agregados estão disponíveis no plano Business.'
      )
    ) {
      setIsFormatModalOpen(false);
      return;
    }
    setIsFormatModalOpen(false);
    setReportModalOpen(true);
  };

  // saas-PDF-C: gera o report analítico após config no ReportConfigModal
  const handleGenerateAnalyticalReport = async (config: ReportConfig) => {
    if (!form) return;
    const selectedList = responses.filter((r) => selectedResponses.has(r.id));
    if (selectedList.length === 0) return;

    setReportGenerating(true);
    setReportProgressLabel('Iniciando...');
    try {
      const useV2 = isFeatureEnabled('pdf.v2', user?.id);
      const mod = useV2
        ? await import('../../lib/pdf/v2/pdfExporter')
        : await import('../../lib/pdf/pdfExporter');
      await mod.exportResponsesAnalyticalReport(selectedList, form, config, {
        onProgress: (label) => setReportProgressLabel(label),
      });
      setReportModalOpen(false);
    } catch (err) {
      console.error('[FormResponses] Falha ao gerar relatório analítico:', err);
      alert('Falha ao gerar relatório analítico. Veja console para detalhes.');
    } finally {
      setReportGenerating(false);
      setReportProgressLabel(null);
    }
  };

  // Batch delete old partial responses (AC6)
  const handleBatchDelete = async () => {
    const days = parseInt(batchDeleteDays, 10);
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    const toDelete = responses.filter(
      r => r.status === 'in_progress' && r.updated_at < cutoff
    );

    setIsBatchDeleting(true);
    for (const r of toDelete) {
      await deleteResponse(r.id);
    }
    setIsBatchDeleting(false);
    setBatchDeleteModal(false);
  };

  const batchDeleteCount = useMemo(() => {
    const days = parseInt(batchDeleteDays, 10);
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    return responses.filter(
      r => r.status === 'in_progress' && r.updated_at < cutoff
    ).length;
  }, [responses, batchDeleteDays]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFieldLabel = (fieldId: string) => {
    const fields = form?.fields;
    if (!Array.isArray(fields)) return fieldId;
    const field = fields.find(f => f.id === fieldId);
    return field?.label || fieldId;
  };

  const formatValue = (value: any): string => {
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (value === null || value === undefined) return '-';
    return String(value);
  };

  // Filter tab counts (AC2)
  const filterCounts = {
    all: stats.total,
    new: stats.new,
    in_progress: stats.inProgress,
    completed: stats.completed,
  };

  if (formLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        {!embedded && (
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-white">Respostas</h1>
          </div>
        )}
        <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/20 text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-400 font-medium">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 rounded-lg bg-neutral-800 text-neutral-300 hover:text-white transition-colors text-sm"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <Reveal variant="fade-up">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 mb-8">
          {!embedded && (
            <div className="flex items-center gap-4 sm:flex-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">{form?.title}</h1>
                <p className="text-neutral-400">Respostas recebidas</p>
              </div>
            </div>
          )}
          {embedded && <div className="hidden sm:block sm:flex-1" />}

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setActiveView('responses')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                activeView === 'responses'
                  ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                  : 'text-neutral-400 hover:text-white bg-neutral-800/50'
              }`}
            >
              Respostas
            </button>
            <button
              onClick={() => setActiveView('webhookLogs')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                activeView === 'webhookLogs'
                  ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                  : 'text-neutral-400 hover:text-white bg-neutral-800/50'
              }`}
            >
              <Webhook className="w-3 h-3" />
              Webhook Logs
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={handleBatchExportClick}
              disabled={selectedResponses.size === 0 || batchProgress !== null || !form}
            >
              {batchProgress ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando PDF... ({batchProgress.current} de {batchProgress.total})
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Exportar selecionados{selectedResponses.size > 0 ? ` (${selectedResponses.size})` : ''}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              disabled={isExportingCsv || !form || responses.length === 0}
            >
              {isExportingCsv ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Exportar CSV
            </Button>
          </div>
        </div>
      </Reveal>

      {/* Webhook Logs View */}
      {activeView === 'webhookLogs' && formId && (
        <WebhookLogViewer formId={formId} />
      )}

      {/* Responses View */}
      {activeView === 'responses' && (
      <>
      {/* Erro do export PDF individual (deck Vecta) — inline, dispensável */}
      {pdfError && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          <p className="text-sm text-red-400 flex-1">{pdfError}</p>
          <button
            onClick={() => setPdfError(null)}
            className="text-xs text-neutral-400 hover:text-white transition-colors"
          >
            Fechar
          </button>
        </div>
      )}
      {/* Stats (AC5) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatsCard
          icon={<CheckCircle2 className="w-6 h-6" />}
          label="Respostas Completas"
          value={stats.completed}
          delay={100}
        />
        <StatsCard
          icon={<Clock className="w-6 h-6" />}
          label="Em Andamento"
          value={stats.inProgress}
          delay={200}
        />
        <StatsCard
          icon={<Percent className="w-6 h-6" />}
          label="Taxa de Conclusão"
          value={`${completionRate}%`}
          delay={300}
        />
        <StatsCard
          icon={<AlertCircle className="w-6 h-6" />}
          label="Abandonadas (24h+)"
          value={abandonedCount}
          delay={400}
        />
      </div>

      {/* Select-all + Filters (saas-1.17 multi-select header + AC2 filtros) */}
      <Reveal variant="fade-up" delay={500}>
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {responses.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-neutral-400 hover:text-white cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selectedResponses.size === responses.length && responses.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-primary-500 focus:ring-primary-500/30"
                aria-label="Selecionar todas as respostas"
              />
              <span>
                {selectedResponses.size === responses.length && responses.length > 0
                  ? 'Desmarcar todas'
                  : `Selecionar todas${selectedResponses.size > 0 ? ` (${selectedResponses.size}/${responses.length})` : ''}`}
              </span>
            </label>
          )}
          <div className="flex gap-2 flex-wrap">
            {(['all', 'new', 'in_progress', 'completed'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`
                  px-4 py-2 rounded-xl text-sm font-medium transition-all
                  ${statusFilter === status
                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                  }
                `}
              >
                {status === 'all' ? 'Todos' : STATUS_CONFIG[status].label}
                <span className="ml-1.5 text-xs opacity-70">({filterCounts[status]})</span>
              </button>
            ))}
          </div>

          {/* Batch delete button (AC6) */}
          {stats.inProgress > 0 && (
            <button
              onClick={() => setBatchDeleteModal(true)}
              className="ml-auto px-3 py-2 rounded-lg text-xs font-medium text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5 inline mr-1.5" />
              Excluir parciais antigas
            </button>
          )}
        </div>
      </Reveal>

      {/* Responses List */}
      {responses.length === 0 ? (
        <EmptyState
          icon={<AlertCircle className="w-10 h-10" />}
          title="Nenhuma resposta ainda"
          description="Quando alguém responder seu formulário, as respostas aparecerão aqui"
        />
      ) : (
        <div className="space-y-4">
          {responses.map((response, index) => {
            const statusConfig = STATUS_CONFIG[response.status] || STATUS_CONFIG.new;
            const StatusIcon = statusConfig.icon;
            const { answered, total } = getFieldCount(response);
            const progressPercent = getProgressPercent(response);
            const isExpanded = expandedResponseId === response.id;
            const answeredFieldIds = new Set(Object.keys(response.data || {}));

            return (
              <Reveal key={response.id} variant="fade-up" delay={600 + index * 50}>
                <div className="liquid-glass rounded-2xl p-6 hover:border-primary-500/30 transition-all group">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    {/* Checkbox de seleção multi-response (saas-1.17) */}
                    <input
                      type="checkbox"
                      checked={selectedResponses.has(response.id)}
                      onChange={() => toggleResponseSelection(response.id)}
                      className="mt-1 w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-primary-500 focus:ring-primary-500/30 shrink-0"
                      aria-label={`Selecionar resposta ${response.id.slice(0, 8)}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {/* Status & Date (AC1) */}
                    <div className="flex items-center gap-3 flex-wrap flex-1">
                      <span
                        className={`
                          flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border
                          ${statusConfig.color}
                        `}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                      {/* Progress count for in_progress (AC1) */}
                      {response.status === 'in_progress' && totalFields > 0 && (
                        <span className="text-xs text-amber-400/80">
                          {answered}/{total} campos
                        </span>
                      )}
                      {response.scores && form?.settings?.scoring?.variables && (
                        <ResponseScoreViewer
                          scores={response.scores}
                          variables={form.settings.scoring.variables}
                        />
                      )}
                      <span className="text-sm text-neutral-500">
                        {formatDate(response.created_at)}
                      </span>
                      {/* Last activity for in_progress (AC3) */}
                      {response.status === 'in_progress' && response.updated_at !== response.created_at && (
                        <span className="text-xs text-neutral-500 italic">
                          Última atividade: {formatDate(response.updated_at)}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <Select
                        value={response.status}
                        onChange={(e) => handleStatusChange(response.id, e.target.value as ResponseStatus)}
                        options={[
                          { value: 'new', label: 'Novo' },
                          { value: 'in_progress', label: 'Em andamento' },
                          { value: 'completed', label: 'Concluído' },
                        ]}
                        className="w-40"
                      />
                      <button
                        onClick={() => setExpandedResponseId(prev => prev === response.id ? null : response.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          isExpanded
                            ? 'text-primary-400 bg-primary-500/10'
                            : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                        }`}
                        title={isExpanded ? 'Recolher' : 'Ver detalhes'}
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleExportPDF(response)}
                        disabled={exportingPdfId === response.id}
                        className="p-2 rounded-lg text-neutral-400 hover:text-primary-400 hover:bg-primary-500/10 transition-colors disabled:opacity-50"
                        title="Exportar PDF"
                      >
                        {exportingPdfId === response.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <FileText className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleExportMarkdown(response)}
                        disabled={exportingMdId === response.id}
                        className="p-2 rounded-lg text-neutral-400 hover:text-primary-400 hover:bg-primary-500/10 transition-colors disabled:opacity-50"
                        title="Exportar MD"
                      >
                        {exportingMdId === response.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <FileCode className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={() => setDeleteModal(response)}
                        className="p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Progress bar for in_progress (AC3) */}
                  {response.status === 'in_progress' && totalFields > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-neutral-400">
                          {answered} de {total} campos preenchidos ({progressPercent}%)
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Response data — show all fields when expanded (AC3) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {isExpanded && totalFields > 0 ? (
                      // Show ALL fields: answered + unanswered (AC3)
                      (form?.fields || []).map(field => {
                        const isAnswered = answeredFieldIds.has(field.id);
                        const value = response.data?.[field.id];
                        return (
                          <div key={field.id} className="bg-neutral-900/50 rounded-xl p-3">
                            <p className="text-xs text-neutral-500 mb-1">
                              {field.label || field.id}
                            </p>
                            {isAnswered ? (
                              <p className="text-sm text-white whitespace-pre-wrap wrap-break-word">
                                {formatValue(value)}
                              </p>
                            ) : (
                              <p className="text-sm text-neutral-600 italic">Não respondido</p>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      // Collapsed: show first 3 answered fields
                      <>
                        {Object.entries(response.data || {})
                          .slice(0, 3)
                          .map(([fieldId, value]) => (
                            <div key={fieldId} className="bg-neutral-900/50 rounded-xl p-3">
                              <p className="text-xs text-neutral-500 mb-1">
                                {getFieldLabel(fieldId)}
                              </p>
                              <p className="text-sm text-white truncate">
                                {formatValue(value)}
                              </p>
                            </div>
                          ))}
                        {Object.keys(response.data || {}).length > 3 && (
                          <button
                            onClick={() => setExpandedResponseId(response.id)}
                            className="bg-neutral-900/50 rounded-xl p-3 flex items-center justify-center hover:bg-neutral-800/50 transition-colors"
                          >
                            <span className="text-sm text-neutral-400">
                              +{Object.keys(response.data || {}).length - 3} campos
                            </span>
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Internal notes (visible when expanded) */}
                  {isExpanded && response.internal_notes && (
                    <div className="mt-4 pt-4 border-t border-neutral-800">
                      <p className="text-xs text-neutral-500 mb-1">Notas internas</p>
                      <p className="text-sm text-neutral-300 whitespace-pre-wrap">{response.internal_notes}</p>
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* Delete Modal */}
      <Modal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Excluir resposta"
        size="sm"
      >
        <div className="space-y-6">
          <p className="text-neutral-400">
            Tem certeza que deseja excluir esta resposta? Esta ação não pode ser desfeita.
          </p>

          <div className="flex gap-4">
            <Button
              variant="outline"
              fullWidth
              onClick={() => setDeleteModal(null)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex-1 px-6 py-3 rounded-full bg-red-500 text-white font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  Excluir
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Batch Delete Modal (AC6) */}
      <Modal
        isOpen={batchDeleteModal}
        onClose={() => setBatchDeleteModal(false)}
        title="Excluir respostas parciais antigas"
        size="sm"
      >
        <div className="space-y-6">
          <p className="text-neutral-400">
            Excluir respostas parciais (<strong>em andamento</strong>) sem atividade há:
          </p>

          <Select
            value={batchDeleteDays}
            onChange={(e) => setBatchDeleteDays(e.target.value)}
            options={BATCH_DELETE_OPTIONS}
          />

          <p className="text-sm text-neutral-500">
            {batchDeleteCount === 0
              ? 'Nenhuma resposta parcial encontrada nesse período.'
              : `${batchDeleteCount} resposta(s) parcial(is) será(ão) excluída(s). Esta ação não pode ser desfeita.`
            }
          </p>

          <div className="flex gap-4">
            <Button
              variant="outline"
              fullWidth
              onClick={() => setBatchDeleteModal(false)}
              disabled={isBatchDeleting}
            >
              Cancelar
            </Button>
            <button
              onClick={handleBatchDelete}
              disabled={isBatchDeleting || batchDeleteCount === 0}
              className="flex-1 px-6 py-3 rounded-full bg-red-500 text-white font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isBatchDeleting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  Excluir {batchDeleteCount}
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Export Format Modal (saas-1.17 — open when selectedResponses.size >= 2) */}
      <ExportFormatModal
        isOpen={isFormatModalOpen}
        onClose={() => setIsFormatModalOpen(false)}
        selectedCount={selectedResponses.size}
        onSelectMultiPage={handleBatchExportMultiPage}
        onSelectAnalytical={handleSelectAnalytical}
        onSelectMarkdown={handleBatchExportMarkdown}
      />

      {/* saas-PDF-C: Analytical Report Config Modal */}
      {form && (
        <ReportConfigModal
          isOpen={reportModalOpen}
          onClose={() => {
            if (!reportGenerating) setReportModalOpen(false);
          }}
          form={form}
          responses={responses.filter((r) => selectedResponses.has(r.id))}
          onExport={handleGenerateAnalyticalReport}
          isGenerating={reportGenerating}
          progressLabel={reportProgressLabel}
        />
      )}
    </div>
  );
};
